import Members from 'pages/application-form-v2/members';
import Identity from 'pages/application-form-v2/identity';
import YourInfo from 'pages/application-form-v2/your-info';
import MoveInDate from 'pages/application-form-v2/move-in-date';
import HousingHistory from 'pages/application-form-v2/housing-history';
import Pets from 'pages/application-form-v2/pets';
import Vehicles from 'pages/application-form-v2/vehicles';
import BackgroundCheck from 'pages/application-form-v2/backgroud-check';
import Section8 from 'pages/application-form-v2/section-8';
import Consent from 'pages/application-form-v2/consent';
import OrgTerms from 'pages/application-form-v2/org-terms';
import AppFee from 'pages/application-form-v2/app-fee';
import AppFeeSuccess from 'pages/application-form-v2/app-fee-success';
import Income from 'pages/application-form-v2/income/income';
import IncomeSources from 'pages/application-form-v2/income-sources';
import IncomeDocuments from 'pages/application-form-v2/income-documents';
import Questions from 'pages/application-form-v2/questions';
import Success from 'pages/application-form-v2/success';
import EmergencyContact from 'pages/application-form-v2/emergency-contact';
import SkipIncome from 'pages/application-form-v2/skip-income-verification';

export const STEP_KEYS = {
  PERSONAL_INFO: 'personalInfo',
  LEASE_INFO: 'leaseInfo',
  ORG_TERMS: 'org-terms',
  IDENTITY: 'identity',
  HOUSING: 'housing',
  BACKGROUND_CHECK: 'backgroundCheck',
  APP_FEE: 'appFee',
  HOUSEHOLD: 'household',
  PETS: 'pets',
  VEHICLES: 'vehicles',
  INCOME_VERIFICATION: 'incomeVerification',
  INCOME: 'income',
  SUBMIT: 'submit',
  SUCCESS: 'success'
};

const isGuarantorType = (application) => ['guarantor'].includes(application?.type);
const requiresEmergencyContact = (location, application) =>
  location?.companyInfo?.requireEmergencyContact && !isGuarantorType(application);

export default ({ translate: t, application, location, companyContext }) => {
  const steps = [
    {
      key: STEP_KEYS.PERSONAL_INFO,
      title: t('overviewStep.steps.personalInfo.title'),
      description: t('overviewStep.steps.personalInfo.description'),
      path: 'about-you',
      routes: [{ path: 'about-you', Component: YourInfo }]
    },
    {
      key: STEP_KEYS.LEASE_INFO,
      title: t('overviewStep.steps.leaseInfo.title'),
      description: requiresEmergencyContact(location, application)
        ? t('overviewStep.steps.leaseInfo.descriptionWithEC')
        : t('overviewStep.steps.leaseInfo.description'),
      path: 'move-in',
      routes: [
        { path: 'move-in', Component: MoveInDate, nextPath: 'emergency-contact' },
        {
          path: 'emergency-contact',
          Component: EmergencyContact,
          backPath: 'move-in',
          hidden: !requiresEmergencyContact(location, application)
        }
      ]
    },
    {
      key: STEP_KEYS.ORG_TERMS,
      title: t('overviewStep.steps.orgTerms.title'),
      description: t('overviewStep.steps.orgTerms.description'),
      path: 'org-terms',
      hidden: !location?.applicationConfig?.applicationConditionsTemplateId,
      routes: [{ path: 'org-terms', Component: OrgTerms }]
    },
    {
      key: STEP_KEYS.IDENTITY,
      title: t('overviewStep.steps.identity.title'),
      description: t('overviewStep.steps.identity.description'),
      path: 'verify-identity',
      routes: [{ path: 'verify-identity', Component: Identity }],
      isDone: ({ app }) => app?.verifiedIdentity,
      isLocked: ({ app }) => app?.verifiedIdentity
    },
    {
      key: STEP_KEYS.HOUSING,
      requireAllPrevious: true,
      title: t('overviewStep.steps.housing.title'),
      description: t('overviewStep.steps.housing.description'),
      path: 'housing-history',
      routes: [
        { path: 'housing-history', Component: HousingHistory, nextPath: 'questions' },
        {
          path: 'questions',
          Component: Questions,
          nextPath: 'section-8',
          backPath: 'housing-history'
        },
        {
          path: 'section-8',
          Component: Section8,
          hidden: !location?.companyInfo?.enableSection8,
          backPath: 'questions'
        }
      ]
    },
    {
      key: STEP_KEYS.BACKGROUND_CHECK,
      title: t('overviewStep.steps.backgroundCheck.title'),
      description: t(
        `ssnInfo.${companyContext?.primaryCountry || 'US'}.backgroundCheck_description`
      ),
      path: 'background-check',
      routes: [{ path: 'background-check', Component: BackgroundCheck }]
    },
    {
      key: STEP_KEYS.APP_FEE,
      title: t('overviewStep.steps.appFee.title'),
      description: t('overviewStep.steps.appFee.description'),
      path: 'fee-details',
      hidden: !application?.chargeApplicants,
      routes: [
        {
          path: 'fee-details',
          Component: AppFee,
          hidden: !application?.chargeApplicants,
          nextPath: 'fee-success'
        },
        {
          path: 'fee-success',
          Component: AppFeeSuccess,
          hidden: !application?.chargeApplicants,
          backPath: 'fee-details',
          autoProceed: true
        }
      ],
      isDone: ({ app }) => !app?.chargeApplicants || (app?.chargeApplicants && app?.paid)
    },
    {
      key: STEP_KEYS.HOUSEHOLD,
      title: t('overviewStep.steps.household.title'),
      description: t('overviewStep.steps.household.description'),
      path: 'members',
      hidden: isGuarantorType(application),
      routes: [{ path: 'members', Component: Members, hidden: isGuarantorType(application) }]
    },
    {
      key: STEP_KEYS.PETS,
      title: t('overviewStep.steps.pets.title'),
      description: t('overviewStep.steps.pets.description'),
      path: 'pets',
      hidden: isGuarantorType(application),
      routes: [
        { path: 'pets', Component: Pets, hidden: isGuarantorType(application), disabled: true }
      ]
    },
    {
      key: STEP_KEYS.VEHICLES,
      title: t('overviewStep.steps.vehicles.title'),
      description: t('overviewStep.steps.vehicles.description'),
      path: 'vehicles',
      hidden: isGuarantorType(application),
      routes: [{ path: 'vehicles', Component: Vehicles, hidden: isGuarantorType(application) }]
    },
    {
      key: STEP_KEYS.INCOME_VERIFICATION,
      title: t('overviewStep.steps.incomeVerification.title'),
      description: t('overviewStep.steps.incomeVerification.description'),
      path: 'income-details',
      routes: [
        {
          path: 'income-details',
          Component: Income,
          hidden: location?.companyContext?.disableBankScreening
        },
        { path: 'skip-income', Component: SkipIncome, backPath: 'income-details' }
      ],
      hidden: !!location?.companyInfo?.disableBankScreening,
      isDone: ({ app }) => {
        return (
          app?.consent?.consented && (app?.verifiedBank || app?.autoIncomeSkipDetails?.skipped)
        );
      }
    },
    {
      key: STEP_KEYS.INCOME,
      title: t('overviewStep.steps.income.title'),
      description: t('overviewStep.steps.income.description'),
      path: 'manual-income-details',
      routes: [
        {
          path: 'manual-income-details',
          Component: IncomeSources,
          nextPath: 'manual-income-documents'
        },
        {
          path: 'manual-income-documents',
          Component: IncomeDocuments,
          backPath: 'manual-income-details'
        }
      ]
    },
    {
      key: STEP_KEYS.SUBMIT,
      title: t('overviewStep.steps.submit.title'),
      description: t('overviewStep.steps.submit.description'),
      path: 'consent-submit',
      isDone: ({ app }) => !!app?.submitted,
      isLocked: ({ app }) => app?.submitted,
      routes: [{ path: 'consent-submit', Component: Consent, autoProceed: true }]
    },
    {
      key: STEP_KEYS.SUCCESS,
      title: t('overviewStep.steps.submit.title'),
      description: t('overviewStep.steps.submit.description'),
      path: 'success',
      hidden: true,
      routes: [{ path: 'success', Component: Success }]
    }
  ];

  return steps.reduce((map, step) => Object.assign(map, { [step.key]: step }), {});
};
